import { FormEvent } from "react";
import { Button, Image, Input } from "@atoms";
import { Partner } from "@api";
import { Oval } from "react-loader-spinner";
import { AuthSection } from "@organisms";
import { useTranslation } from "react-i18next";

export interface FormValues {
  description: string;
  organization: string;
  phoneNumber: string;
  website: string;
}

export interface FormErrorValues {
  description: string | undefined;
  organization: string | undefined;
  phoneNumber: string | undefined;
  website: string | undefined;
}

export type WelcomePageTemplateProps = {
  values: FormValues;
  errors: FormErrorValues;
  loading: boolean;
  handleBackButtonClick: () => Promise<void>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void;
  partner: Partner | null;
  pageIsLoading: boolean;
};

export const WelcomePageTemplate = ({
  values,
  errors,
  loading,
  handleBackButtonClick,
  handleInputChange,
  handleSubmit,
  partner,
  pageIsLoading
}: WelcomePageTemplateProps): JSX.Element => {
  const { t } = useTranslation();

  const handleSendEmailClick = () => {
    window.open("mailto:affiliate@coinmiles.io", "_blank");
  };

  return (
    <div>
      {pageIsLoading ? (
        <div className="min-h-screen flex">
          <AuthSection />

          {/* Right side - Loading content */}
          <div className="w-full lg:w-1/2 min-h-screen flex items-center justify-center bg-white">
            <div className="w-full max-w-md px-8 text-center">
              <div className="mb-8">
                <Oval color="#3B82F6" height={90} width={90} secondaryColor="#1E40AF"/>
              </div>
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Checking Your Account Details
                </h1>
              </div>
              <p className="text-gray-600 text-lg">
                Give us a minute to review the details of your account!
              </p>
            </div>
          </div>
        </div>
      ) : null}
      
      {partner?.organization /* && false */ ? (
        <div className="min-h-screen flex">
          <AuthSection />

          {/* Right side - Pending content */}
          <div className="w-full lg:w-1/2 min-h-screen flex items-center justify-center bg-white">
            <div className="w-full max-w-md px-8 text-center">
              <div className="mb-8 flex justify-center">
                <Image variant="horns" />
              </div>
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Request Pending
                </h1>
              </div>
              <p className="text-gray-600 text-lg mb-6">
                Your request to join Parachute Partners was sent to our Partnership
                Team. We will notify you by email once it has been approved.
              </p>
              <div className="flex justify-center items-center mb-8">
                <span className="text-gray-600 mr-2">Questions?</span>
                <Button
                  variant="link"
                  label="Send us an email!"
                  onClick={handleSendEmailClick}
                  // className="text-lg font-normal text-brand-orange underline hover:text-primary-dark"
                />
              </div>
              <div className="w-full max-w-xs mx-auto">
                <Button
                  variant="primary"
                  label="Back to home"
                  color="brand-orange"
                  hoverColor="primary-dark"
                  textColor="white"
                  onClick={handleBackButtonClick}
                  size="sm"
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="min-h-screen flex">
          <AuthSection />

          {/* Right side - Form content */}
          <div className="w-full lg:w-1/2 min-h-screen flex items-center justify-center bg-white">
            <div className="w-full max-w-md px-8">

              {/* Title */}
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {t("onboardingProfile.title")}
                </h1>
                <p className="text-gray-600 text-sm">
                  {t("onboardingProfile.subtitle")}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  variant="text"
                  name="organization"
                  onChange={handleInputChange}
                  placeholder={t("auth.welcome.organization")}
                  value={values.organization}
                  errorMessage={errors.organization}
                />
                <Input
                  variant="text"
                  name="website"
                  onChange={handleInputChange}
                  placeholder={t("auth.welcome.website")}
                  value={values.website}
                  errorMessage={errors.website}
                />
                <Input
                  variant="text"
                  name="phoneNumber"
                  onChange={handleInputChange}
                  placeholder={t("auth.welcome.phoneNumber")}
                  value={values.phoneNumber}
                  errorMessage={errors.phoneNumber}
                />
                <Input
                  variant="textArea"
                  name="description"
                  onChange={handleInputChange}
                  placeholder={t("auth.welcome.description")}
                  value={values.description}
                  errorMessage={errors.description}
                  height={128}
                />

                {/* Buttons */}
                <div className="space-y-4">
                  <Button
                    variant="primary"
                    label={t("common.continue")}
                    color="brand-orange"
                    hoverColor="primary-dark"
                    textColor="white"
                    stretch
                    onClick={handleSubmit}
                    size="sm"
                    loading={loading}
                  />
                  <Button
                    variant="secondary"
                    label={t("auth.common.backToSignIn")}
                    color="neutral-light"
                    stretch
                    onClick={handleBackButtonClick}
                    size="sm"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
